// @flow

// Skipping story doc for this component since it is a base class component meant only to be extended by its subclasses
/* eslint-disable ta-code-conventions/has-matching-storydoc */

// Skipping a test for this base-class component since the lack of a meaningful computeStateFromStyles() method makes it hard to test,
// and adequate test coverage exists in the component subclass tests.
/* eslint-disable ta-code-conventions/has-matching-test */

import * as React from 'react';
import Localize from '@ta/platform.i18n';
import classNames from '@ta/common.classnames';
// eslint-disable-next-line css-modules/no-unused-class
import styles from './ReadMore.css';

/**
 *  Parameter type for the value passed to the props.ctaClickCallback() function.
 *  The values convey where the click occurred... either on the 'Read more' CTA, or the content itself.
 */
export type ClickTarget = 'CTA' | 'CTA_COLLAPSE' | 'CONTENT';

type State = {|
  collapsed: boolean,
  divHeight: number | string,
|};

/**
 * Props supported by the ReadMoreBase component and its subclasses.
 */
export type Props = {|
  /** Optional CTA text.  Defaults to the "Read more" (localized from key: common_Read_more). */
  ctaText: ?(string | React.Node),
  /** Number of lines to show in the initial collapsed state.  Defaults to 4. */
  numLinesToShow: number,
  /** Optional initial guess at the div's height to prevent jank after calculating styles */
  initialDivHeight: number,
  /** Optional extra css class name(s) applied to the outer div generated by this component. */
  customCssClass: string,
  /** Optional callback invoked when either the 'Read more' CTA is clicked, or the content is clicked.  The target parameter indicates which one was clicked.
   * If the callback returns false, then the component state remains collapsed and the hidden content is not revealed. */
  clickCallback: (target: ClickTarget) => boolean,
  /** Optional element structure used as the CTA. The default is <div><span>...</span><span>{ctaText}</span></div>.  Note that when this property is provided, the value of ctaText will be ignored. */
  cta: ?(React.Node | ((onExpand: () => void) => React.Node)),
  /** Content node(s) */
  children: React.Node,
  /** Optional flag indicating whether the content can be collapsed again after it is expanded */
  collapsible: boolean,
  /** Optional element structure used as the Collapse CTA. The default is <div>{ctaCollapseText}</div>.  Note that when this property is provided, the value of ctaCollapseText will be ignored. */
  ctaCollapse?: React.Node,
  /** Optional Collapse CTA text.  Defaults to the "Read less" (localized from key: common_Readless). */
  ctaCollapseText?: ?(string | React.Node),
  /** Optional flag indicating whether the CTA should be pushed onto a new line. Defaults to false */
  ctaNewline?: boolean,
  /** Optional callback invoked whenever the state of the component changes */
  onStateChange?: State => void,
  /** Always display read more link */
  alwaysShowReadMore?: boolean,
  /** set to control the state via props */
  collapsed?: boolean,
  /** Optional flag to set default CTAs to be ta-accessible-green */
  greenCta?: boolean,
|};

/**
 * Parameter type for the value passed to the computeStateFromStyles() function.
 */
export type ComputedStyles = {|
  /** Current scrollHeight of the content div */
  scrollHeight: number,
  /** Font size of the text in the content div */
  fontSize: number,
  /** Line height of the text in the content div. */
  lineHeight: number,
|};

/**
 * The type of the computeStateFromStyles() function
 */
export type ComputeStateFromStylesFunction = (computedStyles: ComputedStyles) => State;

/**
 * Show a limited number of content text lines, with a 'Read more' link that reveals the remaining content when clicked,
 * or when the content is clicked. This is a base class which provides the common logic and behavior for the subclasses below.
 */
class ReadMoreBase extends React.Component<Props, State> {
  static defaultProps = {
    cta: null,
    ctaText: null,
    numLinesToShow: 4,
    initialDivHeight: 16,
    customCssClass: '',
    // eslint-disable-next-line no-unused-vars
    clickCallback: (target: ClickTarget) => true,
    collapsible: false,
    ctaNewline: false,
    alwaysShowReadMore: false,
    collapsed: undefined,
  };

  static CONTENT = 'CONTENT';
  static CTA = 'CTA';
  static CTA_COLLAPSE = 'CTA_COLLAPSE';

  constructor(props: Props) {
    super(props);
    this.state =
      props.collapsed !== false
        ? { collapsed: true, divHeight: props.initialDivHeight }
        : { collapsed: false, divHeight: 'none' };
    this.contentRef = React.createRef();
    this.ctaStyles = {};
  }

  componentDidMount() {
    if (!this.state.collapsed) {
      return;
    }

    this.computeState();
  }

  componentDidUpdate = (prevProps: Props) => {
    if (
      this.props.collapsed !== undefined &&
      this.props.collapsed !== prevProps.collapsed &&
      !!this.props.collapsed !== this.state.collapsed
    ) {
      if (!this.props.collapsed) {
        this.revealHiddenContent();
      } else {
        this.computeState();
      }
    } else if (
      this.props.numLinesToShow !== prevProps.numLinesToShow ||
      this.props.alwaysShowReadMore !== prevProps.alwaysShowReadMore
    ) {
      this.computeState();
    }
  };

  /**
   * A callback to get the internal state of ReadMore
   *
   */
  setStateCallback = () => {
    if (this.props.onStateChange) {
      this.props.onStateChange(this.state);
    }
  };

  /**
   * Get the scrollHeight attribute for the content DIV.
   * This is intentionally not an arrow function so that we can stub the function from tests, e.g., sinon.sandbox.stub(ReadMoreBase.prototype, 'getScrollHeight');
   */
  getScrollHeight(): number {
    return this.getContentDiv().scrollHeight;
  }

  getContentDiv = () => this.contentRef.current;

  computeState = () => {
    // Find the first parent element containing text content and use that node for the divHeight calculations below.  If
    // a text node parent is not found (i.e., empty <ReadMore/> component), then skip all computations and set the
    // state to collapsed=false so we don't render the 'Read more' link.
    const styleNode = this.findFirstTextNodeParent(this.getContentDiv());
    if (!styleNode) {
      this.revealHiddenContent();
      return;
    }

    // eslint-disable-next-line no-restricted-globals
    const computedStyles = window.getComputedStyle(styleNode);

    // Calculations of the div height for these components are typically a function of line-height.
    // If line-height is undefined or 'normal', then determine an appropriate value based on the font-size.
    let lineHeight = computedStyles.getPropertyValue('line-height');
    let fontSize = computedStyles.getPropertyValue('font-size');
    if (lineHeight && lineHeight !== 'normal') {
      lineHeight = lineHeight.replace('px', '');
    } else {
      fontSize = fontSize ? fontSize.replace('px', '') : 14;
      // TODO: TTD-11236, add support for 'locale' as a prop value, and adjust the fontSize conversion constant below based on the charset
      // Re: use of 1.2 here, see https://developer.mozilla.org/en-US/docs/Web/CSS/line-height
      lineHeight = Math.floor(1.2 * parseFloat(fontSize));
    }

    const styleParams: ComputedStyles = {
      scrollHeight: this.getScrollHeight(),
      fontSize,
      lineHeight,
    };

    const newState = this.computeStateFromStyles(styleParams);

    // If it just fits we don't want to show the CTA
    this.hasCollapsibleContent = styleParams.scrollHeight > lineHeight * this.props.numLinesToShow;

    this.setState({ ...newState }, this.setStateCallback);
  };

  /**
   * This function performs a recursive, depth-first search for the first node that contains a direct child text node.
   * @param currentNode
   * @returns {*}
   */
  findFirstTextNodeParent = (currentNode: Node) => {
    if (!currentNode || !currentNode.childNodes) {
      return null;
    }
    for (let i = 0; i < currentNode.childNodes.length; i++) {
      const childNode = currentNode.childNodes.item(i);
      if (childNode.nodeType === Node.TEXT_NODE) {
        return currentNode;
      }
      const firstTextNodeParent = this.findFirstTextNodeParent(childNode);
      if (firstTextNodeParent) {
        return firstTextNodeParent;
      }
    }
    return null;
  };

  // Reveal the hidden content.
  revealHiddenContent = () => {
    this.setState({ ...this.contentRevealedState }, this.setStateCallback);
  };

  collapseContent = () => {
    this.computeState();
  };

  // Handle clicks in the content and on the 'Read more' CTA link
  handleClick = (target: ClickTarget) => {
    // If the click callback returns false, don't do anything
    if (this.props.clickCallback(target)) {
      switch (target) {
        case ReadMoreBase.CTA:
          this.revealHiddenContent();
          break;

        case ReadMoreBase.CTA_COLLAPSE:
          this.collapseContent();
          break;

        case ReadMoreBase.CONTENT:
          // Reveal the content if collapsed
          if (this.state.collapsed) {
            this.revealHiddenContent();
          }
          break;

        default:
          break;
      }
    }
  };

  // The state we transition to in order to reveal the hidden content
  contentRevealedState: State = { collapsed: false, divHeight: 'none' };

  // Ref to the content DIV
  contentRef: any = null;

  // Styles applied to the 'Read more' (e.g., CTA) link
  ctaStyles: { [string]: string | number };

  // Styles applied to the collapsed read more state. If not provided, ctaStyles above will be used for both the collapsed and uncollapsed state
  collapsedCtaStyles: ?{ [string]: string | number };

  // Variant CSS class set by the concrete sub-class implementations
  variantCssClass: string = '';

  // Whether there is extra content to be revealed
  hasCollapsibleContent: boolean = false;

  computeStateFromStyles: ComputeStateFromStylesFunction = (computedStyles: ComputedStyles) => {
    console.error(
      `${this.constructor.name} did not provide its own implementation of computeStateFromStyles()`,
    );
    console.debug(
      `computedStyles = {scrollHeight: ${computedStyles.scrollHeight}, fontSize: ${
        computedStyles.fontSize
      }, lineHeight: ${computedStyles.lineHeight}`,
    );
    return this.state;
  };

  defaultCTA = () => (
    <div className={styles.cta}>
      <span className={styles.ellipsis} />
      <span>
        <a
          className={classNames(styles.readMoreCtaText, {
            [styles.readMoreCtaGreen]: this.props.greenCta,
          })}
        >
          {this.props.ctaText ? this.props.ctaText : <Localize id="common_Read_more" />}
        </a>
      </span>
    </div>
  );

  defaultCollapseCTA = () => (
    <div>
      <a
        className={classNames(styles.readMoreCtaText, {
          [styles.readMoreCtaGreen]: this.props.greenCta,
        })}
      >
        {this.props.ctaCollapseText ? (
          this.props.ctaCollapseText
        ) : (
          <Localize id="common_Readless" />
        )}
      </a>
    </div>
  );

  render() {
    const onCtaClick = () => this.handleClick(ReadMoreBase.CTA);
    const ctaProp = this.props.cta;
    let CtaComponent;
    if (ctaProp) {
      CtaComponent = typeof ctaProp === 'function' ? () => ctaProp(onCtaClick) : () => ctaProp;
    } else {
      CtaComponent = this.defaultCTA;
    }
    return (
      <div
        className={`${styles.readMore} ${this.variantCssClass} ${this.props.customCssClass} ${
          this.state.collapsed ? styles.collapsed : ''
        }`}
      >
        <div
          ref={this.contentRef}
          className={styles.content}
          style={{ maxHeight: this.state.divHeight }}
          onClick={() => this.handleClick(ReadMoreBase.CONTENT)}
        >
          {this.props.children}
        </div>

        <div
          className={this.props.ctaNewline ? styles.ctaWrapperNewline : styles.ctaWrapper}
          style={this.ctaStyles}
          onClick={() => typeof ctaProp !== 'function' && onCtaClick()}
        >
          <CtaComponent />
        </div>
        {this.props.collapsible &&
          !this.state.collapsed &&
          (this.props.alwaysShowReadMore || this.hasCollapsibleContent) && (
            <div
              className={styles.ctaCollapseWrapper}
              style={this.collapsedCtaStyles || this.ctaStyles}
              onClick={() => this.handleClick(ReadMoreBase.CTA_COLLAPSE)}
            >
              {this.props.ctaCollapse ? this.props.ctaCollapse : this.defaultCollapseCTA()}
            </div>
          )}
      </div>
    );
  }
}

export default ReadMoreBase;
